import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getProduct } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { Reveal } from "@/components/Reveal";
import { Stars } from "@/components/Stars";
import { FaqAccordion } from "@/components/FaqAccordion";
import { Button } from "@/components/ui/button";
import { Check, Download, Lock, ShieldCheck, Infinity as InfinityIcon, ArrowLeft } from "lucide-react";
import { accentFor } from "@/lib/accents";

export default function ProductPage() {
  const { slug } = useParams();
  const { openCheckout } = useStore();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setProduct(null);
    getProduct(slug).then(setProduct).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="mx-auto max-w-7xl px-5 py-32">Product not found. <Link to="/shop" className="underline">Back to shop</Link></div>;
  if (!product) return <div className="mx-auto max-w-7xl px-5 py-32 text-muted-foreground">Loading…</div>;

  const reviews = product.reviews || [];
  const accent = accentFor(product.slug);

  return (
    <div data-testid="product-page">
      <section className="mx-auto max-w-7xl px-5 pt-10 lg:px-8 lg:pt-16">
        <Link to="/shop" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="product-back"><ArrowLeft className="h-4 w-4" /> All guides</Link>
        <div className="mt-8 grid grid-cols-1 gap-12 lg:grid-cols-2">
          <Reveal>
            <div className="relative overflow-hidden border border-black/10 lg:sticky lg:top-32">
              <div className="absolute left-4 top-4 z-10 inline-flex items-center gap-1 bg-white px-3 py-1 text-[10px] uppercase tracking-[0.2em]"><Download className="h-3 w-3" /> Instant Download</div>
              <img src={product.mockup_image} alt={product.title} className="aspect-[4/5] w-full object-cover grayscale" />
            </div>
          </Reveal>
          <div>
            <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-muted-foreground"><span className="inline-block h-2.5 w-2.5" style={{ background: accent }} /> {product.category}</p>
            <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tighter sm:text-5xl">{product.title}</h1>
            <div className="mt-4 h-1 w-16" style={{ background: accent }} />
            <div className="mt-4 flex items-center gap-3"><Stars /> <span className="text-sm text-muted-foreground">Loved by thousands</span></div>
            <p className="mt-6 text-base leading-relaxed text-muted-foreground">{product.description}</p>
            <div className="mt-8 flex items-end gap-3">
              <span className="font-display text-4xl">${product.price?.toFixed(2)}</span>
              {product.compare_at_price && <span className="mb-1 text-lg text-muted-foreground line-through">${product.compare_at_price.toFixed(2)}</span>}
            </div>
            <Button data-testid="product-buy-btn" onClick={() => openCheckout({ item_type: "product", slug: product.slug, title: product.title, price: product.price })}
              className="mt-6 w-full rounded-none h-14 bg-primary text-primary-foreground hover:bg-primary/90">
              <Lock className="h-4 w-4" /> Secure Checkout
            </Button>
            <div className="mt-5 flex flex-wrap gap-5 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1.5"><Download className="h-3.5 w-3.5" /> Instant Digital Download</span>
              <span className="inline-flex items-center gap-1.5"><InfinityIcon className="h-3.5 w-3.5" /> Lifetime Access</span>
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Secure Stripe</span>
            </div>

            <div className="mt-12 space-y-10">
              <div>
                <h2 className="font-display text-xl tracking-tight">What's Included</h2>
                <ul className="mt-4 space-y-2.5">{product.whats_included?.map((x) => <li key={x} className="flex gap-3 text-sm"><Check className="mt-0.5 h-4 w-4" /> {x}</li>)}</ul>
              </div>
              <div>
                <h2 className="font-display text-xl tracking-tight">Who It's For</h2>
                <ul className="mt-4 space-y-2.5">{product.who_its_for?.map((x) => <li key={x} className="flex gap-3 text-sm"><Check className="mt-0.5 h-4 w-4" /> {x}</li>)}</ul>
              </div>
              <div>
                <h2 className="font-display text-xl tracking-tight">Benefits</h2>
                <ul className="mt-4 space-y-2.5">{product.benefits?.map((x) => <li key={x} className="flex gap-3 text-sm"><Check className="mt-0.5 h-4 w-4" /> {x}</li>)}</ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8" data-testid="product-reviews">
        <h2 className="font-display text-3xl tracking-tight sm:text-4xl">Customer Reviews</h2>
        <div className="mt-10 grid grid-cols-1 gap-px overflow-hidden border border-black/10 bg-black/10 md:grid-cols-2">
          {reviews.map((r, i) => (
            <div key={i} className="bg-white p-8">
              <Stars n={r.rating || 5} />
              <p className="mt-4 font-display text-lg leading-snug tracking-tight">"{r.text}"</p>
              <div className="mt-6 flex items-center gap-3">
                <img src={r.avatar} alt={r.name} className="h-10 w-10 rounded-full object-cover grayscale" />
                <p className="text-sm font-medium">{r.name}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-5 pb-24 lg:pb-32">
        <h2 className="font-display text-3xl tracking-tight sm:text-4xl">FAQ</h2>
        <div className="mt-8"><FaqAccordion /></div>
      </section>
    </div>
  );
}
